import { useQuery } from "react-query";
import { kebabCase } from "lodash";
import useQueryState from "./useQueryState";
import { getHybridGeographicalUnitValuesService } from "../services/timeseries";
import { QueryLayer } from "../types/queryState";
import Cookies from "js-cookie";
import { useAuthContext } from "./useAuthContext";
import { useUIStore } from "../store/ui";
import { buildIndicatorName } from "../helpers/indicatorName";

const accessToken = () => Cookies.get("accessToken");

const useGeoUnitHybridValues = ({
  layers,
  season,
  isEnabled,
}: {
  layers: QueryLayer[];
  season: string;
  isEnabled: boolean;
}) => {
  const isDetrended = useUIStore((s) => s.isDetrended);
  const isLandCoverMasked = useUIStore((s) => s.isLandCoverMasked);
  const { parsedToken } = useAuthContext();
  const { email: userEmail } = parsedToken || {};
  const {
    queryState: { country, aggregationLevel, year, layers: queryLayers },
  } = useQueryState();
  const { displayName, parameters } = layers[0];
  const selectedLayer = queryLayers.find(
    (layer) => layer.displayName === "Hybrid Index" && layer.season === season,
  );
  const isSystemType =
    aggregationLevel === "0" ||
    aggregationLevel === "1" ||
    aggregationLevel === "2";

  const getHybridGeographicalUnitValues = useQuery(
    [
      "geoUnit",
      "hybrid",
      country,
      displayName,
      aggregationLevel,
      selectedLayer?.months,
      selectedLayer?.parameters,
      year,
      isSystemType ? "system" : "user",
      isDetrended,
      isLandCoverMasked,
      accessToken() ? userEmail : "",
    ],
    async () => {
      const hybridIndicatorsJson: any = [];
      if (parameters) {
        parameters?.forEach((parameter) => {
          if (parameter.name === "Soil Moisture")
            hybridIndicatorsJson.push({
              indicator: buildIndicatorName(parameter.name, {
                isLandCoverMasked,
                isDetrended,
              }),
              weight: Number(parameter.value),
            });
          else if (parameter.name === "Precipitation")
            hybridIndicatorsJson.push({
              indicator: buildIndicatorName(parameter.name, {
                isLandCoverMasked,
                isDetrended,
              }),
              weight: Number(parameter.value),
            });
          else if (parameter.name === "Vegetation")
            hybridIndicatorsJson.push({
              indicator: buildIndicatorName(parameter.name, {
                isLandCoverMasked,
                isDetrended,
              }),
              weight: Number(parameter.value),
            });
        });
      }
      const response = await getHybridGeographicalUnitValuesService({
        country,
        indicator: buildIndicatorName(displayName, {
          isLandCoverMasked,
          isDetrended,
        }),
        adminLevel: aggregationLevel,
        startmonth: selectedLayer?.months[0] ?? "",
        endmonth: selectedLayer?.months[1] ?? "",
        startDekad: selectedLayer?.dekads[0] ?? "",
        endDekad: selectedLayer?.dekads[1] ?? "",
        hybridIndicatorsJson: JSON.stringify(hybridIndicatorsJson),
        type: isSystemType ? "system" : "user",
      });

      const results: Record<string, number> = {};
      response.data.data
        .filter((item: any) => new Date(item[0]).getFullYear() === year)
        .forEach((item: any[]) => {
          results[item[2]] = item[1];
        });

      return results;
    },
    {
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      retry: false,
      enabled:
        !!country &&
        !!aggregationLevel &&
        layers.length > 0 &&
        !!selectedLayer &&
        selectedLayer?.months.every((m) => !!m) &&
        !!season &&
        selectedLayer?.parameters &&
        Number(selectedLayer?.parameters[0].value) +
          Number(selectedLayer?.parameters[1].value) ===
          100 &&
        selectedLayer?.displayName === "Hybrid Index" &&
        isEnabled,
    },
  );

  return {
    getHybridGeographicalUnitValues,
  };
};

export default useGeoUnitHybridValues;
